// One `Intl.DateTimeFormat` per time zone and seconds option: building one per call is expensive on
// lists that format a date per row.
const formatters = new Map()

const appDateTimeFormatter = (timeZone, includeSeconds) => {
  const cacheKey = `${timeZone ?? ''}|${includeSeconds}`
  if (!formatters.has(cacheKey)) {
    formatters.set(
      cacheKey,
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        day: '2-digit',
        month: 'short',
        year: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...(includeSeconds && { second: '2-digit' }),
      }),
    )
  }
  return formatters.get(cacheKey)
}

/**
 * The app's date and time, "25-Apr 25 at 9:05 AM", in the given time zone. `getFormattedDate`
 * (utility.jsx) and the File Upload Queue both use it, so the format lives in one place.
 * @param {number | Date} timestamp - Epoch ms or a Date.
 * @param {object} [options]
 * @param {string} [options.timeZone] - IANA zone; the browser's own when missing.
 * @param {boolean} [options.includeAt] - "at" between the date and the time (default), else a space.
 * @param {boolean} [options.includeSeconds]
 * @returns {string} Formatted date and time.
 * @throws {RangeError} For an unknown time zone name, like `Intl.DateTimeFormat`.
 */
export function formatAppDateTime(
  timestamp,
  { timeZone, includeAt = true, includeSeconds = false } = {},
) {
  const parts = Object.fromEntries(
    appDateTimeFormatter(timeZone, includeSeconds)
      .formatToParts(timestamp instanceof Date ? timestamp : new Date(timestamp))
      .map((part) => [part.type, part.value]),
  )
  const time = `${parts.hour}:${parts.minute}${includeSeconds ? `:${parts.second}` : ''} ${parts.dayPeriod}`
  return `${parts.day}-${parts.month} ${parts.year}${includeAt ? ' at ' : ' '}${time}`
}
